/**
 * Peer benchmarking for operators (gap G24).
 *
 * Nothing in the product tells an operator whether they are doing well. They
 * see their own numbers going up or down and have no idea whether that is good.
 * A visible, closeable competence gap — "your average ticket is RM 24; carwashes
 * your size are around RM 31" — is the strongest single operator motivator we
 * have, and the cross-tenant data already exists.
 *
 * It is also the most dangerous feature in the platform, because it is the only
 * one where one tenant's numbers inform what another tenant sees. Everything
 * below exists to make a leak structurally impossible rather than merely
 * unlikely.
 *
 * ## Why a median is not automatically safe
 *
 * "Aggregate only" is not sufficient on its own. With four peers, the median of
 * an even-sized set is an average of two real businesses; with five, the median
 * IS one real business's exact number. An operator who knows the cohort is small
 * and knows their own value can reason backwards toward a specific competitor.
 *
 * So three rules compound:
 *
 *   1. A cohort below `MIN_PEER_COHORT` returns NOTHING — not a median, not a
 *      band, not a "close to average". Suppression carries a reason so the UI
 *      can explain the silence instead of rendering an empty card.
 *   2. Peer figures are ROUNDED to a coarse step before they are ever returned,
 *      so a returned number is not any real operator's exact figure.
 *   3. Only the median is ever exposed. No min, no max, no spread, no list —
 *      a maximum is one identifiable business's number however large the cohort.
 *
 * ## Opt-in, and reciprocal
 *
 * An operator sees benchmarks only if they contribute to them. That is not a
 * dark pattern, it is the honest deal: a comparison built from other people's
 * businesses should cost you your own participation. Callers must not include a
 * non-participating tenant's numbers in any cohort.
 *
 * Pure and total: no I/O, no clock. The caller supplies the cohort.
 */

/**
 * Smallest cohort that may produce a benchmark.
 *
 * Not a tuning knob — it is the anonymity floor. Lower it and an operator can
 * start inferring individual competitors; there is no UI copy that makes that
 * acceptable.
 */
export const MIN_PEER_COHORT = 5;

/** Why a benchmark was withheld. Every reason is explainable to the operator. */
export type SuppressionReason =
  /** Fewer than `MIN_PEER_COHORT` comparable businesses. */
  | 'too_few_peers'
  /** This operator has not opted in, so they neither contribute nor receive. */
  | 'not_opted_in'
  /** The operator has no figures of their own yet to compare. */
  | 'no_own_data';

/** Where an operator sits, without naming anyone. */
export type PeerBand = 'ahead' | 'typical' | 'behind';

export type PeerBenchmark =
  | {
      readonly kind: 'suppressed';
      readonly reason: SuppressionReason;
      /**
       * How many more participants are needed, when that is the reason. Lets the
       * UI say something true and hopeful rather than "unavailable".
       */
      readonly needed?: number;
    }
  | {
      readonly kind: 'ranked';
      /** The operator's own figure, unrounded — it is their own data. */
      readonly ownValue: number;
      /** Cohort median, ROUNDED so it is nobody's exact number. */
      readonly peerMedian: number;
      readonly band: PeerBand;
      /** How many businesses the comparison is drawn from. */
      readonly cohortSize: number;
      /**
       * Distance to the median when behind it, rounded the same way. Zero when
       * at or ahead. This is the number the whole feature exists to show: a gap
       * an operator can actually close.
       */
      readonly gapToMedian: number;
    };

export interface PeerBenchmarkInput {
  /** This operator's figure for the metric. */
  readonly ownValue: number;
  /**
   * Comparable businesses' figures, EXCLUDING this operator's own.
   *
   * Excluding it matters: including yourself pulls the median toward you and
   * makes the comparison flatter the closer you are to the edge.
   */
  readonly peerValues: readonly number[];
  /** Whether this operator participates. Non-participants get nothing. */
  readonly optedIn: boolean;
  /**
   * Rounding step for every peer figure returned, in the metric's own units
   * (sen for money, whole washes for counts). Coarse on purpose.
   */
  readonly roundTo: number;
}

/** Round to the nearest step, so a returned figure is nobody's exact number. */
function roundToStep(value: number, step: number): number {
  if (!Number.isFinite(value)) return 0;
  if (!Number.isFinite(step) || step <= 0) return Math.round(value);
  return Math.round(value / step) * step;
}

/** Median of a non-empty numeric list. */
function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

/**
 * How far from the median counts as "typical".
 *
 * A band this wide is deliberate: telling someone they are behind because they
 * are 3% under the median is noise dressed as insight, and it makes the whole
 * card feel arbitrary.
 */
const TYPICAL_TOLERANCE = 0.1;

/**
 * Compare one operator against comparable businesses, or explain why not.
 *
 * Returns a suppression rather than throwing on every unhappy path: "not enough
 * businesses like yours yet" is an ordinary state of a young network, not an
 * error, and the UI needs to say so.
 */
export function peerBenchmark(input: PeerBenchmarkInput): PeerBenchmark {
  if (!input.optedIn) return { kind: 'suppressed', reason: 'not_opted_in' };

  if (!Number.isFinite(input.ownValue) || input.ownValue <= 0) {
    return { kind: 'suppressed', reason: 'no_own_data' };
  }

  const peers = input.peerValues.filter((v) => Number.isFinite(v) && v > 0);
  if (peers.length < MIN_PEER_COHORT) {
    return {
      kind: 'suppressed',
      reason: 'too_few_peers',
      needed: MIN_PEER_COHORT - peers.length,
    };
  }

  return benchmarkAgainstPublished({
    ownValue: input.ownValue,
    optedIn: true,
    roundTo: input.roundTo,
    published: {
      peerMedian: roundToStep(median(peers), input.roundTo),
      cohortSize: peers.length,
    },
  });
}

/**
 * The only thing a client is ever allowed to see about other businesses.
 *
 * Raw peer figures never leave the server: a scheduled job runs `peerBenchmark`
 * over opted-in tenants and publishes THIS — one rounded median and a count —
 * per metric and size band. A dashboard that received peer values could always
 * be made to reveal them, however carefully the UI rendered them.
 */
export interface PublishedPeerStats {
  /** Already rounded by the publisher; nobody's exact figure. */
  readonly peerMedian: number;
  /** How many businesses it was computed from. Never below `MIN_PEER_COHORT`. */
  readonly cohortSize: number;
}

export interface PublishedComparisonInput {
  readonly ownValue: number;
  readonly published: PublishedPeerStats | null;
  readonly optedIn: boolean;
  readonly roundTo: number;
}

/**
 * Compare against an already-published aggregate.
 *
 * Re-checks the anonymity floor rather than trusting the publisher. A stale
 * document written before the floor existed, or by an older deploy, must not be
 * able to talk this into rendering a two-business "median".
 */
export function benchmarkAgainstPublished(input: PublishedComparisonInput): PeerBenchmark {
  if (!input.optedIn) return { kind: 'suppressed', reason: 'not_opted_in' };
  if (!Number.isFinite(input.ownValue) || input.ownValue <= 0) {
    return { kind: 'suppressed', reason: 'no_own_data' };
  }

  const published = input.published;
  if (!published || published.cohortSize < MIN_PEER_COHORT) {
    return {
      kind: 'suppressed',
      reason: 'too_few_peers',
      needed: Math.max(0, MIN_PEER_COHORT - (published?.cohortSize ?? 0)),
    };
  }

  const peerMedian = roundToStep(published.peerMedian, input.roundTo);
  // A degenerate cohort where everyone reported nothing would otherwise make
  // every operator "ahead" by definition.
  if (peerMedian <= 0) {
    return { kind: 'suppressed', reason: 'too_few_peers', needed: MIN_PEER_COHORT };
  }

  const ratio = input.ownValue / peerMedian;
  const band: PeerBand =
    ratio > 1 + TYPICAL_TOLERANCE ? 'ahead' : ratio < 1 - TYPICAL_TOLERANCE ? 'behind' : 'typical';

  return {
    kind: 'ranked',
    ownValue: input.ownValue,
    peerMedian,
    band,
    cohortSize: published.cohortSize,
    gapToMedian: band === 'behind' ? roundToStep(peerMedian - input.ownValue, input.roundTo) : 0,
  };
}

/**
 * Size bands, so an operator is compared with businesses like theirs.
 *
 * A single-bay shop measured against a six-bay operation produces a number that
 * is true and useless. Bands are coarse for the same anonymity reason as
 * everything else: finer bands mean smaller cohorts.
 */
export type SizeBand = 'single' | 'small' | 'medium' | 'large';

export function sizeBandOf(outletCount: number): SizeBand {
  if (!Number.isFinite(outletCount) || outletCount <= 1) return 'single';
  if (outletCount <= 3) return 'small';
  if (outletCount <= 9) return 'medium';
  return 'large';
}
